import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { aliases, mdi } from 'vuetify/iconsets/mdi'
import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'
import './assets/design-system.css'

import router from './router'
import { isRemotePath } from './presentation/joinCode'
import { i18n } from './i18n'

// The signed-in app shares the landing's palette (see assets/design-system.css):
// warm cream/ink, an orange call-to-action accent, and the mosaic colours mapped
// onto Vuetify's semantic roles (teal=success, yellow=warning, blue=info,
// purple=secondary). `tilemeDark` is the same identity inverted for dark mode.
const tilemeLight = {
  dark: false,
  colors: {
    background: '#f6f3ec',
    surface: '#ffffff',
    'surface-bright': '#ffffff',
    'on-background': '#15161b',
    'on-surface': '#15161b',
    primary: '#ff5a3c',
    'on-primary': '#ffffff',
    secondary: '#7c5cff',
    'on-secondary': '#ffffff',
    accent: '#1fc8b6',
    error: '#e5484d',
    info: '#3b8cff',
    success: '#1fc8b6',
    warning: '#ffc23c',
    'on-warning': '#15161b',
  },
  variables: {
    'border-color': '#15161b',
    'border-opacity': 0.08,
  },
}

const tilemeDark = {
  dark: true,
  colors: {
    background: '#121318',
    surface: '#1c1d24',
    'surface-bright': '#26282f',
    'on-background': '#f1efe9',
    'on-surface': '#f1efe9',
    primary: '#ff6a4d',
    'on-primary': '#15161b',
    secondary: '#9a80ff',
    'on-secondary': '#15161b',
    accent: '#2bd4c0',
    error: '#ff6b6b',
    info: '#5b9cff',
    success: '#2bd4c0',
    warning: '#ffce5c',
    'on-warning': '#15161b',
  },
  variables: {
    'border-color': '#ffffff',
    'border-opacity': 0.1,
  },
}

const vuetify = createVuetify({
  components,
  directives,
  icons: { defaultSet: 'mdi', aliases, sets: { mdi } },
  theme: {
    defaultTheme: 'tileme',
    themes: { tileme: tilemeLight, tilemeDark },
  },
  // Component defaults that bake the design language in once: pill buttons,
  // rounded outlined inputs, and flat cards (the soft border + shadow come from
  // design-system.css).
  defaults: {
    VBtn: { rounded: 'pill' },
    VCard: { rounded: 'xl', flat: true },
    VTextField: { variant: 'outlined', rounded: 'lg', color: 'primary' },
    VTextarea: { variant: 'outlined', rounded: 'lg', color: 'primary' },
    VSelect: { variant: 'outlined', rounded: 'lg', color: 'primary' },
    VChip: { rounded: 'lg' },
    VAlert: { rounded: 'lg' },
    VAppBar: { flat: true },
  },
})

async function bootstrap(): Promise<void> {
  const Root = isRemotePath(window.location.pathname)
    ? (await import('./RemoteApp.vue')).default
    : (await import('./App.vue')).default
  createApp(Root).use(createPinia()).use(router).use(vuetify).use(i18n).mount('#app')
}

void bootstrap()
